import * as React from 'react'
// libs
import { useSelector } from 'react-redux'
import { useMutation } from '@apollo/client/react'
import { formValueSelector } from 'redux-form'
// context
import { PlansPageContext } from 'containers/Plan/Register/layers/API'
// constants
import { Forms, ENTITIES } from 'constants/index'
// graphql
import { PLAN_CREATE_MUTATION, PLAN_UPDATE_MUTATION } from 'graphql/plan/mutations'
// hooks
import { useDefaultObjectSecurity } from 'hooks/index'
// types
import { IField, IPlan, IPlanCreateResponse } from 'types/graphql'
// utils
import get from 'lodash/get'
import isFunction from 'lodash/isFunction'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { getFieldValues, getOptionsByEntity, getValuesFromOptions, getValueFromOption } from 'utils/form'

type PlanCreateResponse = {
  planCreate: IPlanCreateResponse
}

export declare namespace NSubPlanStepperAPI {
  interface IContext {
    createdPlan: IPlan
    createPlan: (data: any, fields: IField[], plan: IPlan) => Promise<void>
    parentPlan: IPlan
    steps: string[]
    updatePlan: (data: any, fields: IField[]) => Promise<void>
  }

  interface IIncomingProps {
    children?: React.ReactNode
    parentPlan: IPlan
  }
}

export const SubPlanStepperAPIContext = React.createContext<NSubPlanStepperAPI.IContext>({} as any)

const SubPlanStepperAPI: React.FunctionComponent<NSubPlanStepperAPI.IIncomingProps> = ({ parentPlan, children }) => {
  const [planCreateMutation] = useMutation<PlanCreateResponse>(PLAN_CREATE_MUTATION)
  const [planUpdateMutation] = useMutation(PLAN_UPDATE_MUTATION)

  const [createdPlan, setCreatedPlan] = React.useState(null)

  const { refetch } = React.useContext(PlansPageContext)

  const { planType } = useSelector((state) => ({
    planType: formValueSelector(Forms.CREATE_SUB_PLAN_FORM)(state, 'planType')
  }))

  const defaultObjectSecurities = useDefaultObjectSecurity({
    objectApiName: 'plans',
    planTypeId: get(planType, 'value')
  })

  // methods
  const createPlan = async (data, fields, plan) => {
    try {
      const {
        data: {
          planCreate: {
            data: { id, companyId, name }
          }
        }
      } = await planCreateMutation({
        variables: {
          input: {
            fieldValues: getFieldValues(fields, data, defaultObjectSecurities),
            memberPositionIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.POSITION)),
            memberUserIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.USER)),
            name: data.planTitle,
            ownedById: getValueFromOption(data.ownedByEmployeeOrPosition),
            parentPlanId: get(plan, 'id'),
            planTypeId: getValueFromOption(data.planType),
            status: getValueFromOption(data.status)
          }
        }
      })
      setCreatedPlan({ id, companyId, name })

      if (isFunction(refetch)) {
        await refetch()
      }
    } catch (error) {
      console.log('[SubPlanStepperAPI.createPlan]: ', parseGraphQLErrorMessages(error))
    }
  }

  const updatePlan = async (data, fields) => {
    try {
      await planUpdateMutation({
        variables: {
          id: createdPlan.id,
          input: {
            fieldValues: getFieldValues(fields, data, defaultObjectSecurities),
            memberPositionIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.POSITION)),
            memberUserIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.USER)),
            name: data.planTitle,
            ownedById: getValueFromOption(data.ownedByEmployeeOrPosition),
            planTypeId: getValueFromOption(data.planType),
            status: getValueFromOption(data.status)
          }
        }
      })

      setCreatedPlan({ ...createdPlan, name: data.planTitle })

      if (isFunction(refetch)) {
        await refetch()
      }
    } catch (error) {
      console.log('[SubPlanStepperAPI.updatePlan]: ', parseGraphQLErrorMessages(error))
    }
  }

  const steps = [
    'Lets start by specifying key information for this sub plan',
    `Now select items from the plan "${get(parentPlan, 'name')}" that we wish to add into the "${get(
      createdPlan,
      'name'
    )}" plan`
  ]

  const value = {
    createdPlan,
    createPlan,
    parentPlan,
    steps,
    updatePlan
  }

  return <SubPlanStepperAPIContext.Provider value={value}>{children}</SubPlanStepperAPIContext.Provider>
}

export default SubPlanStepperAPI
